import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom, SCOPES, useHasScope } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type GraphFiles = {
  download_url: string;
};

/**
 * キャリブレーション候補値グラフデータ取得API
 */
export const useGetCalibrationParameterGraphFilesAPI = (): {
  state: APIState;
  getCalibrationParameterGraphFiles: (
    vehicle_id: string,
    target: string,
  ) => Promise<GraphFiles | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const fmsAPI = useFMSAPI();
  const getHasScope = useHasScope();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const getCalibrationParameterGraphFilesAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      target: string,
    ): Promise<Result<GraphFiles, AxiosResponse>> => {
      try {
        const res = await fmsAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/calibration_parameter_candidates/graph_file_download_links?target=${target}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const getCalibrationParameterGraphFiles = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, target: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) && isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.get_calibration_parameter_graph_files', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        if (!getHasScope(SCOPES.DownloadCalibrationParameter)) {
          setState('hasError');
          return null;
        }
        const res = await getCalibrationParameterGraphFilesAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
          target,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.get_calibration_parameter_graph_files', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        setState('hasValue');
        return res.value;
      },
      [
        notifyError,
        getCalibrationParameterGraphFilesAPI,
        getHasScope,
        t,
        getErrorMessage,
      ],
    ),
  );

  return {
    state,
    getCalibrationParameterGraphFiles,
  };
};
